/**
 * Order and wait (BLU-1506): why units of a blank line are marked, shared by the
 * To buy card's next order and the PO page's lines. Out at the supplier last
 * time, short in a box, and (the PO page) how many units still wait.
 */
import { useTranslation } from 'react-i18next';
import type { OrderAndWaitPoLine } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { OrderAndWaitStatusChip } from './OrderAndWaitStatusChip';

interface OrderAndWaitLineMarksProps {
  line: Pick<OrderAndWaitPoLine, 'outUnits' | 'shortUnits' | 'waitingUnits'>;
  supplierName: string | null;
  /** Add "N waiting" after the chips. */
  showWaiting?: boolean;
  /** A plain line shows a dash (not on a phone, where this mark sits under the name) instead of nothing. */
  placeholder?: boolean;
  className?: string;
}

export function OrderAndWaitLineMarks({
  line,
  supplierName,
  showWaiting = false,
  placeholder = false,
  className,
}: OrderAndWaitLineMarksProps) {
  const { t } = useTranslation('orders');
  const waitingUnits = showWaiting ? line.waitingUnits : 0;
  if (line.outUnits === 0 && line.shortUnits === 0 && waitingUnits === 0) {
    return placeholder ? (
      <span className={cn('text-xs text-muted-foreground', className, 'max-sm:hidden')}>-</span>
    ) : null;
  }
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {line.outUnits > 0 && <OrderAndWaitStatusChip status="out" supplierName={supplierName} />}
      {line.shortUnits > 0 && <OrderAndWaitStatusChip status="short" />}
      {waitingUnits > 0 && (
        <span className="text-xs text-muted-foreground">{t('oaw.page.waitingUnits', { count: waitingUnits })}</span>
      )}
    </div>
  );
}
